// Bienvenida según la hora local del perfil (America/El_Salvador por defecto). Dentro de cada franja se
// elige una variante por día: cambia de un día a otro, pero no en cada recarga.

export interface Greeting {
  title: string;
  note: string;
}

interface LocalTime {
  hour: number;
  minute: number;
  weekday: number; // 0 = domingo
  dayOfYear: number;
}

function localTime(now: Date, timeZone: string): LocalTime {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', hour: 'numeric', minute: 'numeric', weekday: 'short', year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(now).map((part) => [part.type, part.value]));
  const year = Number(parts.year);
  const start = Date.UTC(year, 0, 1);
  const day = Date.UTC(year, Number(parts.month) - 1, Number(parts.day));
  return {
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    weekday: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(String(parts.weekday)),
    dayOfYear: Math.round((day - start) / 86_400_000),
  };
}

type Variant = (name: string, clock: string) => Greeting;

const LATE_NIGHT: Variant[] = [
  (name) => ({ title: `¿Qué haces despierto a esta hora, ${name}?`, note: 'Hasta los búhos pidieron descanso. Atiende lo urgente y a dormir.' }),
  (name, clock) => ({ title: `Turno de búho, ${name}`, note: `Son las ${clock}. Que el café esté fuerte y la bandeja tranquila.` }),
  (name) => ({ title: `${name}, esto ya es madrugada`, note: 'Los clientes pueden esperar al desayuno; tu sueño no tanto.' }),
];
const EARLY: Variant[] = [
  (name) => ({ title: `¡Madrugaste, ${name}!`, note: 'Ni el gallo se ha levantado todavía. Te ganaste el primer café del día.' }),
  (name) => ({ title: `Buenos días, ${name}`, note: 'Llegaste antes que el sol. La bandeja está lista cuando tú lo estés.' }),
];
const MORNING: Variant[] = [
  (name) => ({ title: `Buenos días, ${name}`, note: 'Que sea un buen día de ventas.' }),
  (name) => ({ title: `Buenos días, ${name}`, note: 'Empecemos: ¿qué conversación toca primero?' }),
  (name) => ({ title: `¡Buen día, ${name}!`, note: 'Café servido, bandeja abierta. A darle.' }),
];
const LUNCH: Variant[] = [
  (name) => ({ title: `¿Ya almorzaste, ${name}?`, note: 'Buen provecho. Las conversaciones te esperan aquí.' }),
  (name) => ({ title: `Buenas tardes, ${name}`, note: 'Hora de almuerzo: nadie atiende bien con el estómago vacío.' }),
];
const AFTERNOON: Variant[] = [
  (name) => ({ title: `Buenas tardes, ${name}`, note: 'Un cafecito y seguimos.' }),
  (name) => ({ title: `Buenas tardes, ${name}`, note: 'La tarde rinde: vamos por esas respuestas.' }),
];
const FRIDAY_AFTERNOON: Variant[] = [
  (name) => ({ title: `Buenas tardes, ${name}`, note: '¡Ya casi es fin de semana! Cerremos bien la semana.' }),
];
const EVENING: Variant[] = [
  (name) => ({ title: `Buenas noches, ${name}`, note: 'Gracias por quedarte un rato más.' }),
  (name) => ({ title: `Buenas noches, ${name}`, note: 'Últimas respuestas del día y a descansar.' }),
];
const LATE: Variant[] = [
  (name) => ({ title: `Buenas noches, ${name}`, note: 'Ya es tarde: cierra lo pendiente y descansa.' }),
  (name) => ({ title: `Se hace tarde, ${name}`, note: 'Mañana la bandeja seguirá aquí. Tú también deberías descansar.' }),
];
const WEEKEND: Variant[] = [
  (name) => ({ title: `Hola, ${name}`, note: 'Trabajando en fin de semana: el equipo te lo agradece.' }),
  (name) => ({ title: `Feliz fin de semana, ${name}`, note: 'Atiende lo necesario y date un respiro.' }),
];

function bandOf({ hour, weekday }: LocalTime): Variant[] {
  if (hour < 5) return LATE_NIGHT;
  if (hour < 7) return EARLY;
  const weekend = weekday === 0 || weekday === 6;
  if (hour < 12) return weekend ? WEEKEND : MORNING;
  if (hour < 14) return LUNCH;
  if (hour < 19) return weekday === 5 ? FRIDAY_AFTERNOON : weekend ? WEEKEND : AFTERNOON;
  if (hour < 22) return EVENING;
  return LATE;
}

export function greetingFor(displayName: string, timeZone: string, now: Date = new Date()): Greeting {
  const name = displayName.trim().split(/\s+/)[0] || displayName;
  const local = localTime(now, timeZone);
  const variants = bandOf(local);
  const clock = `${String(local.hour).padStart(2, '0')}:${String(local.minute).padStart(2, '0')}`;
  return variants[local.dayOfYear % variants.length]!(name, clock);
}
